import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PromoBar from "@/components/PromoBar";
import WeeklyThemeBanner from "@/components/WeeklyThemeBanner";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import About from "@/components/sections/About";
import CarlosEMilenaSection from "@/components/sections/CarlosEMilenaSection";
import FinalCTA from "@/components/sections/FinalCTA";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const SOBRE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "Sobre Carlos e Milena Medeiros",
  description:
    "Carlos e Milena Medeiros lideram o Círculo Renascer, dedicado ao cuidado pastoral de casais e famílias.",
};

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Carlos e Milena Medeiros",
  url: "https://cemedeiros.com.br/",
  description:
    "Espaço diário de fé criado por Carlos e Milena Medeiros. Devocional aprofundado, cuidado pastoral de casais e famílias, planos de leitura.",
};

function PalavraDeHojeHero() {
  return (
    <section
      data-testid="home-hero-devocional"
      style={{
        paddingTop:
          "calc(var(--promo-bar-height, 0px) + var(--weekly-banner-height, 0px) + 6.5rem)",
      }}
      className="relative bg-[#F7F5F0] pb-14 md:pb-16 overflow-hidden"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, #C5A059 0%, transparent 30%), radial-gradient(circle at 85% 80%, #0B1E36 0%, transparent 40%)",
        }}
      />
      <div className="relative max-w-4xl mx-auto px-6 md:px-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <span className="h-px w-8 bg-[#C5A059]/60" aria-hidden />
          <p className="text-[0.7rem] md:text-[0.75rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium">
            Palavra do Dia
          </p>
          <span className="h-px w-8 bg-[#C5A059]/60" aria-hidden />
        </div>
        <h1 className="font-serif text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.15] tracking-tight text-[#0B1E36] mb-4 md:mb-5">
          Comece o dia com <em className="italic text-[#7B3F00]">uma palavra viva</em>.
        </h1>
        <p className="text-[0.95rem] md:text-base text-[#0B1E36]/70 font-light max-w-2xl mx-auto leading-relaxed mb-7 md:mb-8">
          Um devocional aprofundado, com contexto histórico, verdade central e aplicação concreta.
          Escrito para alcançar seu coração hoje.
        </p>
        <Link
          to="/palavra-de-hoje"
          data-testid="home-cta-palavra-de-hoje"
          className="inline-flex items-center gap-2 bg-[#0B1E36] hover:bg-[#132C4D] text-white font-semibold text-[0.78rem] md:text-[0.85rem] uppercase tracking-[0.18em] px-6 md:px-8 py-3.5 md:py-4 transition-colors duration-300 shadow-[0_10px_30px_-10px_rgba(11,30,54,0.35)]"
        >
          Leia a Palavra de hoje
          <ArrowRight size={16} strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}

export default function Sobre({ isHome = false }) {
  useDocumentMeta(
    isHome
      ? {
          title: "Carlos e Milena Medeiros, um espaço diário de fé",
          description:
            "Espaço diário de fé criado por Carlos e Milena Medeiros. Devocional aprofundado, cuidado pastoral de casais e famílias, planos de leitura.",
          canonical: "https://cemedeiros.com.br/",
          url: "https://cemedeiros.com.br/",
          jsonLd: HOME_JSON_LD,
        }
      : {
          title: "Sobre, Carlos e Milena Medeiros",
          description:
            "Conheça o ministério de Carlos e Milena Medeiros no cuidado pastoral de casais e famílias através do Círculo Renascer.",
          canonical: "https://cemedeiros.com.br/sobre",
          url: "https://cemedeiros.com.br/sobre",
          jsonLd: SOBRE_JSON_LD,
        }
  );
  return (
    <div
      data-testid={isHome ? "home-sobre-page" : "sobre-page"}
      className="bg-cream min-h-screen"
    >
      <PromoBar />
      <WeeklyThemeBanner mode="top" />
      <Navbar />
      {isHome ? (
        <main>
          <PalavraDeHojeHero />
          <About />
          <CarlosEMilenaSection />
          <FinalCTA />
        </main>
      ) : (
        <main className="pt-32">
          <About />
          <CarlosEMilenaSection />
          <FinalCTA />
        </main>
      )}
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
